import { Box, createStyles, makeStyles, Typography } from '@material-ui/core';
import cn from 'classnames';
import * as React from 'react';
import CertificationCardIcon from '../../assets/icons/CertificationCardIcon';
import Loader from '../../assets/icons/Loader';
import { TranquiTheme } from '../../containers/App/theme';
import RenderText from '../../dictionaries/RenderText';
import { BankType } from '../../generated/graphql';
import { session } from '../../utils/auth';
import { getServerURLBase } from '../../utils/getServerURLBase';
import Alert from '../Alert/Alert';
import Btn from '../Btn/Btn';
import Modal from '../Modal/Modal';
import Separator from '../Separator/Separator';

interface CertificationCardProps {
  className?: string;
  noBalance?: boolean;
  bank?: BankType;
  negotiationId: string;
}

const CertificationCard: React.FC<CertificationCardProps> = (props) => {
  const { className, noBalance, bank, negotiationId } = props;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const [ showModal, setShowModal ] = React.useState(false);
  const [ loading, setLoading ] = React.useState(false);
  const [ error, setError ] = React.useState(false);
  const noBalanceText = noBalance ? 'noBalance_' : '';

  const handleDownload = async () => {
    setLoading(true);
    setError(false);

    try {
      const response = await fetch(
        `${getServerURLBase()}/api/negotiations/certificates/${negotiationId}`,
        { headers: session.headers },
      );

      if (!response.ok) throw new Error();

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      // Nested try/finally ensures revokeObjectURL is called even if DOM operations fail,
      // preventing memory leaks from unreleased blob URLs
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = `certificado_negociacion_Tranqui_${bank ?? 'Banco'}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setShowModal(false);
      } finally {
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError(false);
  };

  return (
    <>
      <Separator />
      <Box className={cn(classes.root, className)}>
        <Modal
          onClose={handleCloseModal}
          maxWidth="sm"
          scroll="paper"
          open={showModal}
          showCloseBtn
        >
          <Typography className={classes.modalTitle}>
            <RenderText
              textKey={`${noBalanceText}certification_card_title_modal`}
              isMarkdown
            />

          </Typography>
          <Typography className={classes.modalText}>
            <RenderText
              textKey={`${noBalanceText}certification_card_description_modal`}
              isMarkdown
            />
          </Typography>
          {error && !loading && (
            <Alert color="error" icon="info" className="mt-2">
              <RenderText
                textKey="certification_card_download_error"
              />
            </Alert>
          )}
          <Btn
            onClick={handleDownload}
            className={classes.downloadBtn}
            disabled={loading}
          >
            {loading ? <Loader size={20} /> : (
              <RenderText
                textKey="certification_card_download_btn"
              />
            )}
          </Btn>
        </Modal>
        <CertificationCardIcon className="mb-1" />
        <Typography className={classes.title}>
          <RenderText
            textKey={`${noBalanceText}certification_card_title`}
            isMarkdown
          />
        </Typography>
        <Typography className={classes.text}>
          <RenderText
            textKey={`${noBalanceText}certification_card_description`}
            context={bank ? { bank } : undefined}
            isMarkdown
          />
        </Typography>
        <Btn onClick={() => setShowModal(true)}>
          <RenderText
            textKey={`${noBalanceText}certification_card_btn`}
            isMarkdown
          />
        </Btn>
      </Box>
    </>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    padding: theme.spacing(0, 3),
    boxSizing: 'border-box',
    maxWidth: 500,
    margin: 'auto',
  },
  modalTitle: {
    ...theme.typography.h6,
    fontWeight: 600,
    color: theme.palette.indigo.main,
    marginBottom: theme.spacing(1),
  },
  modalText: {
    ...theme.typography.body1,
    color: theme.palette.text4.main,
  },
  title: {
    ...theme.typography.body1,
    fontWeight: 600,
    color: theme.palette.indigo.main,
    marginBottom: theme.spacing(2),
  },
  text: {
    ...theme.typography.body2,
    color: theme.palette.text2.main,
    marginBottom: theme.spacing(3),
  },
  downloadBtn: {
    marginTop: theme.spacing(2),
    minWidth: 120,
  },
}));

export default CertificationCard;
